import { Injectable, signal } from '@angular/core';

import { AssistantMessage } from '../models/assistant.model';

/**
 * One exchange carried from a widget into the full chat.
 *
 * `question` is what the reader asked, in their own words where there are any.
 * `answer` is the summary they have already read, verbatim — so the chat opens
 * showing the conversation they were in rather than restarting it.
 */
export interface AssistantHandover {
  question: string;
  answer: string;
}

/**
 * Carries the summary a reader has already read into the assistant page.
 *
 * The reader types in the homepage hero, lands on the search page, reads the
 * summary the widget streamed there, and then asks a follow-up — which opened
 * an empty chat and sent the follow-up with no history. So the summary vanished
 * from the screen, and the agent never saw its own earlier answer: "and what
 * does it cost?" referred to something it could not read.
 *
 * **Why a service and not the URL.** An answer is hundreds of characters, and
 * the URL is shared, bookmarked and read aloud. `?q=` stays — it is the
 * question, and it is meaningful to anyone who receives the link — while the
 * transcript rides in memory.
 *
 * **Why not `sessionStorage`.** The handover must NOT survive a reload. Kept
 * anywhere durable, a later direct visit to `/assistant` would open holding a
 * conversation from half an hour ago, with no way for the reader to tell where
 * it came from. In memory and consumed once gives both properties for nothing:
 * a reload loses it, and so does a second visit.
 */
@Injectable({ providedIn: 'root' })
export class AssistantHandoverService {
  private readonly _pending = signal<AssistantHandover | null>(null);

  /**
   * Hand an exchange to the chat. Replaces anything already waiting: only the
   * most recent one can be the conversation the reader is actually in.
   */
  offer(handover: AssistantHandover): void {
    this._pending.set(handover);
  }

  /**
   * Take the waiting exchange, if any, and clear it.
   *
   * Reading it consumes it, which is what stops a transcript reappearing when
   * the reader comes back to `/assistant` later by another route.
   */
  take(): AssistantMessage[] {
    const handover = this._pending();
    this._pending.set(null);
    if (!handover) {
      return [];
    }
    const question = handover.question.trim();
    const answer = handover.answer.trim();
    // Both halves or neither. An answer with no question would render an
    // assistant turn nothing led to, and would reach the model as a history
    // that opens on its own voice.
    if (!question || !answer) {
      return [];
    }
    return [
      { role: 'user', content: question },
      { role: 'assistant', content: answer },
    ];
  }
}
